import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { userService } from '../services/database/userService';
import { achievementService } from '../services/studyHistory/AchievementService';
import { API_CONFIG } from '../config/api.config';
import {
  StudyProgress,
  QuizResult,
  Achievement,
  UserStatistics
} from '../types/learningHistory';
import { 
  User, 
  Mail, 
  Calendar, 
  MapPin, 
  Phone, 
  Edit3, 
  Save, 
  ArrowLeft,
  Shield,
  CheckCircle,
  BookOpen,
  Target,
  Trophy,
  BarChart3,
  Clock,
  TrendingUp,
  Flame,
  FileText,
  GraduationCap,
  Settings,
  Download
} from 'lucide-react';
import { Link } from 'react-router-dom';
import TeacherContentPanel from '../components/teacher/TeacherContentPanel';
import TeacherStudentsTab from '../components/teacher/TeacherStudentsTab';
import MembershipCard from '../components/membership/MembershipCard';

type ActiveTab = 'info' | 'history' | 'stats' | 'content';

const ProfilePage: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const desktopAppDownloadUrl = `${API_CONFIG.python.apiUrl}/lesson-builder/recorder/download/windows`;
  const [activeTab, setActiveTab] = useState<ActiveTab>('info');
  const [isEditing, setIsEditing] = useState(false);
  const [profileData, setProfileData] = useState({
    displayName: '',
    phone: '',
    address: '',
    bio: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  
  // Learning history data
  const [loading, setLoading] = useState(false);
  const [statistics, setStatistics] = useState<UserStatistics | null>(null);
  const [lessonProgress, setLessonProgress] = useState<StudyProgress[]>([]);
  const [quizHistory, setQuizHistory] = useState<QuizResult[]>([]);
  const [essayHistory, setEssayHistory] = useState<any[]>([]);          // bài tập tự luận đã chấm
  const [essayStats, setEssayStats] = useState<{ count: number; avg: number }>({ count: 0, avg: 0 });
  const [achievements, setAchievements] = useState<Achievement[]>([]);

  useEffect(() => {
    const loadUserProfile = async () => {
      if (user) {
        try {
          // Load existing profile data from database
          const existingProfile = await userService.getUserProfile(user.id);
          if (existingProfile) {
            setProfileData({
              displayName: existingProfile.fullName || user.name || '',
              phone: existingProfile.phone || '',
              address: existingProfile.address || '',
              bio: existingProfile.bio || ''
            });
          } else {
            // Fallback to user data if no profile exists
            setProfileData(prev => ({
              ...prev,
              displayName: user.name || ''
            }));
          }
        } catch (error) {
          console.error('Error loading user profile:', error);
          // Fallback to user data
          setProfileData(prev => ({
            ...prev,
            displayName: user.name || ''
          }));
        }
      }
    };

    loadUserProfile();
  }, [user]);

  // Tự động chuyển tab nếu giáo viên đang ở tab history
  useEffect(() => {
    if (user?.role === 'teacher' && activeTab === 'history') {
      setActiveTab('info');
    }
  }, [user?.role, activeTab]);

  // Load learning history data when switching to history tab (chỉ cho học sinh)
  useEffect(() => {
    if (activeTab === 'history' && user?.id && user?.role !== 'teacher') {
      loadLearningHistory();
    }
  }, [activeTab, user?.id, user?.role]);

  const loadLearningHistory = async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      // NGUỒN SỰ THẬT = backend /study (nơi bài giảng e-learning ghi tiến độ) → đồng bộ đa thiết bị.
      const base = API_CONFIG.python.apiUrl;
      const token = localStorage.getItem('jwt_token') || localStorage.getItem('auth_token') || localStorage.getItem('token') || '';
      const authFetch = async (path: string) => {
        try { const r = await fetch(`${base}${path}`, { headers: { Authorization: `Bearer ${token}` } }); return r.ok ? await r.json() : null; }
        catch { return null; }
      };
      const [stats, progList, quizList, assessRes, achievs] = await Promise.all([
        authFetch('/study/progress/stats'),
        authFetch('/study/progress'),
        authFetch('/study/quiz-results?limit=50'),
        authFetch('/study/my-assessments'),   // đề PMaker + bài tập tự luận đã chấm
        achievementService.getUserAchievements(user.id).catch(() => []),
      ]);
      const quizzes: any[] = Array.isArray(quizList) ? quizList : [];
      const assess: any[] = Array.isArray(assessRes?.items) ? assessRes.items : [];
      const exams = assess.filter((a) => a.type === 'exam' || a.type === 'lesson_quiz');  // TRẮC NGHIỆM (PMaker + quiz trong bài)
      const essays = assess.filter((a) => a.type === 'assignment');   // TỰ LUẬN

      // Điểm quy về thang 10
      const q10 = (q: any) => (Number(q.totalQuestions) > 0 ? (Number(q.correctAnswers) / Number(q.totalQuestions)) * 10 : Number(q.score) || 0);
      const a10 = (a: any) => (Number(a.maxScore) > 0 ? (Number(a.score) / Number(a.maxScore)) * 10 : Number(a.score) || 0);
      const mean = (arr: number[]) => (arr.length ? arr.reduce((s, x) => s + x, 0) / arr.length : 0);

      // TRẮC NGHIỆM = quiz trong bài giảng + đề PMaker
      const quizScore10 = [...quizzes.map(q10), ...exams.map(a10)];
      setStatistics({
        completedLessons: stats?.completed_lessons || 0,
        totalLessons: stats?.total_lessons || 0,
        totalQuizzes: quizzes.length + exams.length,
        averageScore: mean(quizScore10),
        totalStudyTime: stats?.total_time_spent || 0,
        achievements: (achievs as any) || [],
      } as unknown as UserStatistics);

      // TỰ LUẬN (riêng)
      setEssayStats({ count: essays.length, avg: mean(essays.map(a10)) });

      setLessonProgress((Array.isArray(progList) ? progList : [])
        .sort((a: any, b: any) => new Date(b.last_accessed_at || b.updated_at || 0).getTime() - new Date(a.last_accessed_at || a.updated_at || 0).getTime())
        .map((p: any) => ({
          id: p.id, lessonId: p.lesson_id, lessonTitle: p.lesson_title || p.lesson_id,
          progress: Math.min(100, Math.round(p.progress || 0)),
          timeSpent: p.time_spent || 0, status: p.status,
        })) as any);

      const quizItems = quizzes.map((q: any) => ({
        id: q.id, quizTitle: q.quizTitle || q.quiz_title || 'Trắc nghiệm', attemptNumber: q.attemptNumber || 1,
        passed: q.passed, score: q.score,
        correctAnswers: q.correctAnswers ?? q.correct_answers ?? 0,
        wrongAnswers: q.wrongAnswers ?? q.wrong_answers ?? 0,
        _scoreText: '', _date: q.completedAt || q.createdAt || q.created_at,
      }));
      const examItems = exams.map((a: any) => ({
        id: a.id, quizTitle: (a.type === 'lesson_quiz' ? '🧩 ' : '📝 ') + a.title, attemptNumber: 1,
        passed: a.maxScore > 0 ? a.score >= 0.5 * a.maxScore : true,
        score: Math.round(a10(a) * 10) / 10, correctAnswers: 0, wrongAnswers: 0,
        _scoreText: a.type === 'lesson_quiz' ? `Đúng ${a.score}/${a.maxScore} câu` : `Điểm ${a.score}/${a.maxScore || 10}`,
        _date: a.submitted_at,
      }));
      setQuizHistory([...quizItems, ...examItems]
        .sort((x: any, y: any) => new Date(y._date || 0).getTime() - new Date(x._date || 0).getTime()) as any);

      setEssayHistory(essays.map((a: any) => ({
        id: a.id, title: a.title,
        passed: a.maxScore > 0 ? a.score >= 0.5 * a.maxScore : true,
        scoreText: `Điểm ${a.score}/${a.maxScore || 10}`, _date: a.submitted_at,
      })).sort((x: any, y: any) => new Date(y._date || 0).getTime() - new Date(x._date || 0).getTime()));

      setAchievements((achievs as any) || []);
    } catch (error) {
      console.error('❌ Failed to load learning history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    if (!user) return;
    
    setIsSaving(true);
    try {
      // Update user profile in database
      const updatedProfile = await userService.updateUserProfile(user.id, {
        fullName: profileData.displayName,
        phone: profileData.phone,
        address: profileData.address,
        bio: profileData.bio,
      });
      
      console.log('Profile updated successfully:', updatedProfile);
      setIsEditing(false);
      alert('Đã lưu thông tin thành công!');
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Có lỗi xảy ra khi lưu thông tin. Vui lòng thử lại.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isAuthenticated || !user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Vui lòng đăng nhập</h1>
          <Link to="/" className="text-blue-600 hover:text-blue-700">
            Quay về trang chủ
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex items-center space-x-4">
            <Link 
              to="/" 
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="text-sm sm:text-base">Quay lại</span>
            </Link>
            <div className="h-4 sm:h-6 w-px bg-gray-300"></div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Thông tin cá nhân</h1>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Lối tắt học tập (chuyển từ menu chính vào Profile) */}
        <div className="flex flex-wrap gap-3 mb-6">
          <Link
            to="/dang-hoc"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-bold shadow-sm hover:bg-blue-700 transition-colors no-underline"
          >
            <GraduationCap className="w-4 h-4" />
            Đang học
          </Link>
          {(user?.role === 'teacher' || user?.role === 'admin') && (
            <>
              <Link
                to="/teacher/khoa-hoc"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white text-slate-700 border border-slate-200 text-sm font-bold shadow-sm hover:bg-slate-50 transition-colors no-underline"
              >
                <Settings className="w-4 h-4" />
                Không gian giáo viên
              </Link>
              <a
                href={desktopAppDownloadUrl}
                download="Ebook-Vatli365-Setup.exe"
                target="_blank"
                rel="noopener"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-bold shadow-sm hover:bg-emerald-700 transition-colors no-underline"
              >
                <Download className="w-4 h-4" />
                Tải Ebook Vatli365 cho Windows
              </a>
            </>
          )}
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-xl shadow-sm border mb-6">
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab('info')}
              className={`flex-1 px-6 py-4 text-sm font-semibold transition-colors ${
                activeTab === 'info'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <User className="w-4 h-4 inline-block mr-2" />
              Thông tin cá nhân
            </button>
            {user?.role === 'teacher' && (
              <button
                onClick={() => setActiveTab('stats')}
                className={`flex-1 px-6 py-4 text-sm font-semibold transition-colors ${
                  activeTab === 'stats'
                    ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <BarChart3 className="w-4 h-4 inline-block mr-2" />
                Thống kê học sinh
              </button>
            )}
            {user?.role !== 'teacher' && (
              <button
                onClick={() => setActiveTab('history')}
                className={`flex-1 px-6 py-4 text-sm font-semibold transition-colors ${
                  activeTab === 'history'
                    ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <BookOpen className="w-4 h-4 inline-block mr-2" />
                Lịch sử học tập
              </button>
            )}
            {user?.role === 'teacher' && (
              <button
                onClick={() => setActiveTab('content')}
                className={`flex-1 px-6 py-4 text-sm font-semibold transition-colors ${
                  activeTab === 'content'
                    ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <FileText className="w-4 h-4 inline-block mr-2" />
                Quản lý nội dung
              </button>
            )}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'content' && user?.role === 'teacher' ? (
          <TeacherContentPanel />
        ) : activeTab === 'stats' && user?.role === 'teacher' ? (
          <TeacherStudentsTab />
        ) : activeTab === 'history' && user?.role !== 'teacher' ? (
          // History Tab Content - Chỉ hiển thị cho học sinh
          <div>
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Đang tải lịch sử học tập...</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Statistics Overview */}
                {statistics && (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    {/* Total Lessons */}
                    <div className="bg-white rounded-xl shadow-sm border p-6">
                      <div className="flex items-center justify-between mb-2">
                        <BookOpen className="w-8 h-8 text-blue-500" />
                        <span className="text-3xl font-bold text-gray-800">
                          {statistics.completedLessons}
                        </span>
                      </div>
                      <h3 className="text-gray-600 text-sm">Bài học hoàn thành</h3>
                      <p className="text-xs text-gray-500 mt-1">/ {statistics.totalLessons} bài</p>
                    </div>

                    {/* Total Quizzes */}
                    <div className="bg-white rounded-xl shadow-sm border p-6">
                      <div className="flex items-center justify-between mb-2">
                        <Target className="w-8 h-8 text-green-500" />
                        <span className="text-3xl font-bold text-gray-800">
                          {statistics.totalQuizzes}
                        </span>
                      </div>
                      <h3 className="text-gray-600 text-sm">Bài trắc nghiệm</h3>
                      <p className="text-xs text-gray-500 mt-1">Điểm TB: {statistics.averageScore.toFixed(1)}</p>
                    </div>

                    {/* Bài tự luận (riêng) */}
                    <div className="bg-white rounded-xl shadow-sm border p-6">
                      <div className="flex items-center justify-between mb-2">
                        <FileText className="w-8 h-8 text-teal-500" />
                        <span className="text-3xl font-bold text-gray-800">{essayStats.count}</span>
                      </div>
                      <h3 className="text-gray-600 text-sm">Bài tự luận</h3>
                      <p className="text-xs text-gray-500 mt-1">Điểm TB: {essayStats.avg.toFixed(1)}</p>
                    </div>

                    {/* Study Time */}
                    <div className="bg-white rounded-xl shadow-sm border p-6">
                      <div className="flex items-center justify-between mb-2">
                        <Clock className="w-8 h-8 text-purple-500" />
                        <span className="text-2xl font-bold text-gray-800">
                          {Math.floor(statistics.totalStudyTime / 3600)}h {Math.floor((statistics.totalStudyTime % 3600) / 60)}p
                        </span>
                      </div>
                      <h3 className="text-gray-600 text-sm">Thời gian học</h3>
                      <p className="text-xs text-gray-500 mt-1">
                        {Math.floor(statistics.totalStudyTime / 60)} phút
                      </p>
                    </div>

                    {/* Achievements */}
                    <div className="bg-white rounded-xl shadow-sm border p-6">
                      <div className="flex items-center justify-between mb-2">
                        <Trophy className="w-8 h-8 text-yellow-500" />
                        <span className="text-3xl font-bold text-gray-800">
                          {statistics.achievements.length}
                        </span>
                      </div>
                      <h3 className="text-gray-600 text-sm">Huy hiệu</h3>
                      <p className="text-xs text-gray-500 mt-1">Thành tích đạt được</p>
                    </div>
                  </div>
                )}

                {/* Recent Lessons */}
                {lessonProgress.length > 0 && (
                  <div className="bg-white rounded-xl shadow-sm border p-6">
                    <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                      <BookOpen className="w-5 h-5 text-blue-500" />
                      Bài học gần đây
                    </h3>
                    <div className="space-y-3">
                      {lessonProgress.slice(0, 5).map(lesson => (
                        <div key={lesson.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                          <div className="flex-1">
                            <div className="font-medium text-gray-800 text-sm">{lesson.lessonTitle}</div>
                            <div className="text-xs text-gray-500">{lesson.lessonId}</div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <div className="text-sm font-semibold text-blue-600">{lesson.progress}%</div>
                              <div className="text-xs text-gray-500">
                                {Math.floor(lesson.timeSpent / 60)}p {lesson.timeSpent % 60}s
                              </div>
                            </div>
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              lesson.status === 'completed' ? 'bg-green-100 text-green-800' :
                              lesson.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {lesson.status === 'completed' ? 'Hoàn thành' :
                               lesson.status === 'in_progress' ? 'Đang học' : 'Chưa bắt đầu'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <Link 
                      to="/lich-su-hoc-tap"
                      className="mt-4 block text-center text-blue-600 hover:text-blue-700 text-sm font-medium"
                    >
                      Xem tất cả →
                    </Link>
                  </div>
                )}

                {/* Recent Quizzes */}
                {quizHistory.length > 0 && (
                  <div className="bg-white rounded-xl shadow-sm border p-6">
                    <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                      <Target className="w-5 h-5 text-green-500" />
                      Trắc nghiệm gần đây
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {quizHistory.slice(0, 4).map(quiz => (
                        <div key={quiz.id} className="p-4 bg-gray-50 rounded-lg">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <h4 className="font-medium text-gray-800 text-sm">{quiz.quizTitle}</h4>
                              <p className="text-xs text-gray-500">Lần {quiz.attemptNumber}</p>
                            </div>
                            <div className={`text-2xl font-bold ${
                              quiz.passed ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {quiz.score}
                            </div>
                          </div>
                          <div className="flex items-center justify-between text-xs text-gray-600">
                            {(quiz as any)._scoreText
                              ? <span>{(quiz as any)._scoreText}</span>
                              : <>
                                  <span>✓ {quiz.correctAnswers} đúng</span>
                                  <span>✗ {quiz.wrongAnswers} sai</span>
                                </>}
                            <span className={quiz.passed ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                              {quiz.passed ? 'Đạt' : 'Chưa đạt'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <Link 
                      to="/lich-su-hoc-tap"
                      className="mt-4 block text-center text-blue-600 hover:text-blue-700 text-sm font-medium"
                    >
                      Xem tất cả →
                    </Link>
                  </div>
                )}

                {/* Recent Essays (tự luận) */}
                {essayHistory.length > 0 && (
                  <div className="bg-white rounded-xl shadow-sm border p-6">
                    <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-teal-500" />
                      Bài tự luận gần đây
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {essayHistory.slice(0, 4).map((es: any) => (
                        <div key={es.id} className="p-4 bg-gray-50 rounded-lg flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-gray-800 text-sm truncate">✍️ {es.title}</h4>
                            <p className="text-xs text-gray-500">{es.scoreText}</p>
                          </div>
                          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${es.passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {es.passed ? 'Đạt' : 'Chưa đạt'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Achievements */}
                {achievements.length > 0 && (
                  <div className="bg-white rounded-xl shadow-sm border p-6">
                    <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                      <Trophy className="w-5 h-5 text-yellow-500" />
                      Thành tích ({achievements.length})
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {achievements.slice(0, 8).map(achievement => (
                        <div key={achievement.id} className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-lg p-4 border-2 border-yellow-200 text-center">
                          <div className="text-4xl mb-2">{achievement.icon}</div>
                          <h4 className="text-xs font-bold text-gray-800">{achievement.title}</h4>
                        </div>
                      ))}
                    </div>
                    {achievements.length > 8 && (
                      <Link 
                        to="/lich-su-hoc-tap"
                        className="mt-4 block text-center text-blue-600 hover:text-blue-700 text-sm font-medium"
                      >
                        Xem tất cả {achievements.length} thành tích →
                      </Link>
                    )}
                  </div>
                )}

                {/* Empty State */}
                {!statistics && lessonProgress.length === 0 && quizHistory.length === 0 && achievements.length === 0 && (
                  <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
                    <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-gray-800 mb-2">Chưa có lịch sử học tập</h3>
                    <p className="text-gray-600 mb-6">Bắt đầu học để xem tiến độ của bạn!</p>
                    <Link 
                      to="/"
                      className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Bắt đầu học
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : activeTab === 'info' ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-8">
          {/* Profile Card */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border p-4 sm:p-6">
              <div className="text-center">
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <span className="text-white font-bold text-2xl sm:text-3xl drop-shadow-sm">
                    {user.name?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                </div>
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-1">
                  {user.name}
                </h2>
                <p className="text-xs sm:text-sm text-gray-500 mb-4 break-all">{user.email}</p>
                
                {/* Verification Status */}
                <div className="flex items-center justify-center space-x-2 bg-green-50 text-green-700 px-3 py-2 rounded-lg">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-xs sm:text-sm font-medium">Đã xác thực Google</span>
                </div>
              </div>
            </div>

            {/* Gói PRO + Điểm */}
            <div className="mt-4 sm:mt-6">
              <MembershipCard showPoints={true} />
            </div>

            {/* Quick Stats */}
            <div className="bg-white rounded-xl shadow-sm border p-4 sm:p-6 mt-4 sm:mt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Thống kê</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600 text-sm sm:text-base">Ngày tham gia</span>
                  <span className="font-medium text-sm sm:text-base">
                    {user.createdAt ? new Date(user.createdAt).toLocaleDateString('vi-VN') : 'Không xác định'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 text-sm sm:text-base">Trạng thái</span>
                  <span className="font-medium text-green-600 text-sm sm:text-base">Hoạt động</span>
                </div>
              </div>
            </div>
          </div>

          {/* Profile Details */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm border">
              <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-3 sm:space-y-0">
                  <h3 className="text-lg font-semibold text-gray-900">Thông tin chi tiết</h3>
                  {!isEditing ? (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors w-full sm:w-auto text-sm sm:text-base"
                    >
                      <Edit3 className="w-4 h-4" />
                      <span>Chỉnh sửa</span>
                    </button>
                  ) : (
                    <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 w-full sm:w-auto">
                      <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 w-full sm:w-auto text-sm sm:text-base"
                      >
                        {isSaving ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span>Đang lưu...</span>
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4" />
                            <span>Lưu</span>
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => setIsEditing(false)}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors w-full sm:w-auto text-sm sm:text-base"
                      >
                        Hủy
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="p-4 sm:p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  {/* Email */}
                  <div className="space-y-2">
                    <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                      <Mail className="w-4 h-4 text-blue-500" />
                      <span>Email</span>
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-gray-900">{user.email}</p>
                      <p className="text-xs text-gray-500 mt-1">Email từ tài khoản Google</p>
                    </div>
                  </div>

                  {/* Display Name */}
                  <div className="space-y-2">
                    <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                      <User className="w-4 h-4 text-green-500" />
                      <span>Tên hiển thị</span>
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={profileData.displayName}
                        onChange={(e) => handleInputChange('displayName', e.target.value)}
                        className="w-full px-3 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                        placeholder="Nhập tên hiển thị"
                      />
                    ) : (
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-gray-900 text-sm sm:text-base">{profileData.displayName || user.name}</p>
                      </div>
                    )}
                  </div>

                  {/* Phone */}
                  <div className="space-y-2">
                    <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                      <Phone className="w-4 h-4 text-purple-500" />
                      <span>Số điện thoại</span>
                    </label>
                    {isEditing ? (
                      <input
                        type="tel"
                        value={profileData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        className="w-full px-3 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                        placeholder="Nhập số điện thoại"
                      />
                    ) : (
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-gray-900 text-sm sm:text-base">{profileData.phone || 'Chưa cập nhật'}</p>
                      </div>
                    )}
                  </div>

                  {/* Address */}
                  <div className="space-y-2">
                    <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                      <MapPin className="w-4 h-4 text-red-500" />
                      <span>Địa chỉ</span>
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={profileData.address}
                        onChange={(e) => handleInputChange('address', e.target.value)}
                        className="w-full px-3 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                        placeholder="Nhập địa chỉ"
                      />
                    ) : (
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-gray-900 text-sm sm:text-base">{profileData.address || 'Chưa cập nhật'}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Bio */}
                <div className="mt-6 space-y-2">
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                    <Calendar className="w-4 h-4 text-orange-500" />
                    <span>Giới thiệu bản thân</span>
                  </label>
                  {isEditing ? (
                    <textarea
                      rows={4}
                      value={profileData.bio}
                      onChange={(e) => handleInputChange('bio', e.target.value)}
                      className="w-full px-3 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base resize-none"
                      placeholder="Viết vài dòng giới thiệu về bản thân..."
                    />
                  ) : (
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-gray-900 whitespace-pre-wrap text-sm sm:text-base">
                        {profileData.bio || 'Chưa cập nhật'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        ) : null}
      </div>
    </div>
  );
};

export default ProfilePage;
